import { QueryClient, keepPreviousData } from '@tanstack/react-query';
import type { QueryKey, FetchQueryOptions } from '@tanstack/react-query';
import { logger } from '../../utils/logger';

// Re-export
export { queryKeyFactory } from './queryKeys';
export type { QueryKeyFactory, InferQueryKey } from './queryKeys';

/**
 * Основной QueryClient для приложения
 * Настроен с оптимальными параметрами для планирования и логистики
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Время, в течение которого данные считаются свежими
      staleTime: 5 * 60 * 1000, // 5 минут

      // Глобальный дефолт: 30 минут достаточно для рабочей сессии.
      // Персистируемые ключи (rbac, feature-flags) имеют собственный gcTime
      // в queryOptionsFactory.ts и не зависят от этого значения.
      gcTime: 30 * 60 * 1000, // 30 минут
      
      // Retry отключён на уровне QueryClient — логика повторов уже реализована
      // в fetchWithErrorHandling (ApiErrorHandler.ts): exponential backoff,
      // 429/5xx retry, без retry для 4xx. Двойной retry = до 9 попыток.
      retry: false,
      
      // Не перезапрашиваем при фокусе окна — свежесть данных обеспечивается через
      // WebSocket invalidation и периодическую проверку консистентности (60с).
      refetchOnWindowFocus: false,
      
      // Перезапрашиваем при восстановлении соединения
      refetchOnReconnect: true,
      
      // Перезапрашиваем при монтировании компонента
      refetchOnMount: true,
      
      // Показываем предыдущие данные при загрузке
      placeholderData: keepPreviousData,
    },
    
    mutations: {
      // Retry отключён — мутации не должны повторяться автоматически:
      // повторная запись при неизвестном состоянии сервера может привести к дублям.
      retry: false,

      // Обработка ошибок
      onError: (error) => {
        logger.mutation.error('mutation', error);
      },
    },
  },
});

/**
 * Утилиты для работы с QueryClient
 */
export const queryClientUtils = {
  /**
   * Инвалидирует все запросы по ключу
   */
  invalidateQueries: (queryKey: QueryKey) => {
    queryClient.invalidateQueries({ queryKey });
  },
  
  /**
   * Очищает все данные из кеша
   */
  clearCache: () => {
    queryClient.clear();
  },
  
  /**
   * Получает данные из кеша
   */
  getQueryData: <T>(queryKey: QueryKey) => {
    return queryClient.getQueryData<T>(queryKey);
  },
  
  /**
   * Устанавливает данные в кеш
   */
  setQueryData: <T>(queryKey: QueryKey, data: T) => {
    queryClient.setQueryData(queryKey, data);
  },
  
  /**
   * Удаляет запрос из кеша
   */
  removeQueries: (queryKey: QueryKey) => {
    queryClient.removeQueries({ queryKey });
  },
  
  /**
   * Prefetch запроса (загрузка данных заранее)
   * Идеально для предзагрузки данных следующего этапа
   */
  prefetchQuery: async <T>(
    options: FetchQueryOptions<T>
  ) => {
    await queryClient.prefetchQuery(options);
  },
  
  /**
   * Обеспечивает наличие данных в кеше перед рендерингом
   * Используется вместе с useSuspenseQuery для Suspense-first подхода
   */
  ensureQueryData: async <T>(
    options: FetchQueryOptions<T>
  ) => {
    return queryClient.ensureQueryData(options);
  },
  
  /**
   * Отменяет активные запросы по ключу
   * Полезно при быстром переключении между вкладками/этапами
   */
  cancelQueries: async (queryKey: QueryKey) => {
    await queryClient.cancelQueries({ queryKey });
  },
  
  /**
   * Проверяет, загружаются ли данные по ключу
   */
  isFetching: (queryKey: QueryKey) => {
    return queryClient.isFetching({ queryKey }) > 0;
  },
  
  /**
   * Обновляет данные в кеше оптимистично
   * Используется для мгновенного отклика UI при мутациях
   */
  optimisticUpdate: <T>(
    queryKey: QueryKey,
    updater: (old: T | undefined) => T
  ) => {
    queryClient.setQueryData<T>(queryKey, updater);
  },
};


